'use client';

import React from 'react';
import { motion } from 'framer-motion';
import Link from 'next/link'; 
import { Zap, Activity, CheckCircle2, ShieldCheck, ArrowRight } from 'lucide-react';

// --- Brand Colors & Constants ---
// Using Zinc for neutral grays often looks cleaner on screens than standard Gray
const TEXT_MUTED = 'text-zinc-300'; 
const TEXT_HIGHLIGHT = 'text-white';
const GOLD_ACCENT = '#faaf40';

const features = [
    {
        title: 'EMS Training',
        subtitle: 'Efficiency Redefined',
        icon: Zap,
        description: 'Our FDA-approved Electrical Muscle Stimulation (EMS) technology activates 90% of your muscle fibers simultaneously. Achieve a full-body workout in just 20 minutes.',
        benefit: '20 Mins = 3 Hours in Gym',
    },
    {
        title: 'Pilates Reformer',
        subtitle: 'Core & Alignment',
        icon: Activity,
        description: 'Realign your spine and build distinct core strength. Our Reformer studio focuses on low-impact, high-precision movements to sculpt long, lean muscle.',
        benefit: 'Posture Correction & Flexibility',
    },
    {
        title: 'Guaranteed Results',
        subtitle: 'The Aura Promise',
        icon: ShieldCheck,
        description: 'We are so confident in our science-backed protocol that we offer a transformation guarantee. Follow our custom plan, and you will see the change.',
        benefit: '100% Transformation Guarantee',
    },
];

export default function ServicesGuarantee() {
    return (
        <section className="bg-[#0a0a0a] text-white py-16 md:py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">

            {/* --- Ambient Background Glow --- */}
            {/* Positioned for mobile and desktop to add depth without distraction */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[300px] md:w-[800px] h-[300px] md:h-[500px] bg-[#faaf40] opacity-[0.06] blur-[100px] md:blur-[140px] rounded-full pointer-events-none" />

            <div className="max-w-7xl mx-auto relative z-10">

                {/* --- HEADER --- */}
                <div className="text-center max-w-3xl mx-auto mb-16 lg:mb-24">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/10 bg-white/5 backdrop-blur-sm mb-6"
                    >
                        <CheckCircle2 className="w-4 h-4 text-[#faaf40]" />
                        <span className="text-[10px] sm:text-xs font-bold tracking-widest uppercase text-white">
                            Science-Backed Methodology
                        </span>
                    </motion.div>

                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.1 }}
                        // "text-balance" ensures the text doesn't leave one word on a new line
                        className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-6 text-balance"
                    >
                        We Don't Guess. <br className="hidden sm:block" />
                        <span className="text-[#faaf40]">We Transform.</span>
                    </motion.h2>

                    <motion.p
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.2 }}
                        className={`text-base sm:text-lg md:text-xl ${TEXT_MUTED} leading-relaxed max-w-2xl mx-auto text-balance`}
                    >
                        At Aura Wellness Clubs, we combine advanced <span className={TEXT_HIGHLIGHT}>EMS technology</span> with the precision of <span className={TEXT_HIGHLIGHT}>Pilates</span> to deliver a guaranteed transformation. No wasted time, just results.
                    </motion.p>
                </div>

                {/* --- FEATURES GRID --- */}
                {/* Responsive Grid: 1 col mobile, 3 cols tablet/desktop */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
                    {features.map((item, idx) => (
                        <motion.div
                            key={item.title}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: idx * 0.1 }} // Slightly faster stagger for mobile feel
                            className="group relative flex flex-col h-full bg-[#141414] border border-white/5 rounded-2xl p-6 sm:p-8 hover:border-[#faaf40]/50 transition-colors duration-300"
                        >
                            {/* Icon Container */}
                            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-black rounded-xl border border-white/10 flex items-center justify-center mb-6 group-hover:scale-105 transition-transform duration-300 shadow-lg relative z-20">
                                <item.icon className="w-6 h-6 sm:w-7 sm:h-7 text-white group-hover:text-[#faaf40] transition-colors" />
                            </div>

                            <div className="flex-grow relative z-20">
                                <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">{item.title}</h3>
                                <p className="text-xs font-bold text-[#faaf40] uppercase tracking-wider mb-4">
                                    {item.subtitle}
                                </p>
                                <p className={`text-sm sm:text-base ${TEXT_MUTED} leading-relaxed mb-6`}>
                                    {item.description}
                                </p>
                            </div>

                            {/* Benefit Footer */}
                            <div className="pt-6 mt-auto border-t border-white/5 relative z-20">
                                <div className="flex items-center gap-3">
                                    <div className="w-2 h-2 rounded-full bg-[#faaf40] animate-pulse flex-shrink-0" />
                                    <span className="text-sm font-bold text-white group-hover:text-[#faaf40] transition-colors">
                                        {item.benefit}
                                    </span>
                                </div>
                            </div>

                            {/* Hover Gradient Effect */}
                            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#faaf40]/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                        </motion.div>
                    ))}
                </div>

                {/* --- CTA SECTION --- */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.4 }}
                    className="mt-16 md:mt-24 text-center"
                >
                    <div className="inline-block p-[1px] rounded-full bg-gradient-to-r from-white/10 via-[#faaf40] to-white/10 hover:via-[#faaf40] transition-all duration-500">
                        <Link 
                            href="/join" 
                            aria-label="Start your fitness transformation journey"
                            className="group relative inline-flex items-center gap-3 px-8 sm:px-10 py-4 rounded-full bg-black hover:bg-[#1a1a1a] text-white font-bold tracking-widest uppercase transition-all duration-300 hover:shadow-[0_0_25px_rgba(250,175,64,0.25)]"
                        >
                            <span>Start Your Transformation</span>
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform text-[#faaf40]" />
                        </Link>
                    </div>
                    
                    <p className={`mt-6 text-[10px] sm:text-xs ${TEXT_MUTED} max-w-md mx-auto`}>
                        *Consultation required for transformation guarantee program. Results vary based on adherence to protocol.
                    </p>
                </motion.div>

            </div>
        </section>
    );
}